import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Card,
  ListGroup,
  Modal,
  Button,
  Form,
  Spinner,
  Badge,
} from "react-bootstrap";
import { useEffect, useState } from "react";
import { useGetEventUsers } from "../../api/queries/useGetEventUsers";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useAppContext } from "../../Context/AppContext";
import { useRemoveBooking } from "../../api/mutations/useRemoveBooking";
import { LuDownload } from "react-icons/lu";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { Users } from "../../types/Types";
import { useTranslation } from "react-i18next";
import {
  getEventObjectiveDisplayLabel,
  getEventStageDisplayLabel,
} from "../../i18n/eventRegistrationI18n";

export interface EventUser {
  // Event-specific fields
  firstName: string;
  lastName: string;
  email: string;
  stageOfBusiness: string;
  eventObjectives: string;
  sectorsOfInterest: string;
  consentToShareProfile: number;
  userId: string;
  // All User fields (optional, in case API returns them)
  id?: number;
  isAdmin?: number;
  objectId?: string;
  isGDPRConsent?: number;
  isEmailUpdates?: number;
  isAnnualMembership?: number;
  membershipId?: number;
  membershipName?: string;
  isDirectory?: number;
  isTermConditions?: number;
  hasPaid?: number;
  dateOfBirth?: string;
  idDocument?: string;
  phoneNumber?: string;
  city?: string;
  country?: string;
  gender?: string;
  languagePreference?: string;
  professionalPhoto?: string;
  aboutYou?: string;
  educationLevel?: string;
  studyInterest?: string;
  skillsExperience?: string;
  mentorshipObjectives?: string;
  mentorshipExpertise?: string;
  mentorshipFormat?: string;
  platformInterest?: string;
  peerNetworking?: string;
  productInterested?: string;
  businessName?: string;
  businessLogo?: string;
  businessSectorIndustry?: string;
  businessWebsiteUrl?: string;
  businessCountryOperation?: string;
  businessAbout?: string;
  businessAddressLineOne?: string;
  businessAddressLineTwo?: string;
  businessCountry?: string;
  businessPostCode?: string;
  businessTelephoneNumber?: string;
  businessCurrency?: string;
  businessTurnover?: string;
  businessObjectives?: string;
  businessProductServices?: string;
  businessDiscount?: string;
  businessDiscountCode?: string;
  personalGender?: string;
  personalLanguagePreference?: string;
  personalAbout?: string;
  personalProfessionalPhoto?: string;
  personalPositionCompany?: string;
  hasCompletedProfile?: number;
  county?: string;
  IdDocument?: string;
  PersonalProfessionalPhoto?: string;
  BusinessLogo?: string;
  ProfessionalPhoto?: string;
  DiscountCode?: string;
  DiscountEndDate?: string;
  bannerImage?: string;
  BannerImage?: string;
  universityCollege?: string;
  referralCode?: string;
  referralCodeUsed?: string;
  lastLoggedIn?: string;
  isWelcomeEmailSent?: number;
  createdDate?: string;
}

const EventDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setViewMode } = useAppContext();
  const { data: EventUsers, refetch: fetchEventUsers } = useGetEventUsers({
    id: id || "",
  });
  const { data: eventsData } = useGetEvent({ eventId: id || "" });
  const [selectedUser, setSelectedUser] = useState<EventUser | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cancelModal, setCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

  const { mutate: removeBooking } = useRemoveBooking({
    onSuccess: () => {
      setCancelModal(false);
      setSelectedUser(null);
      setShowRefundModal(true);
      fetchEventUsers();
    },
  });

  const handleCancelBooking = () => {
    if (selectedUser) {
      removeBooking({
        userId: selectedUser.objectId || selectedUser.userId,
        eventId: parseInt(id || "0"),
      });
    }
  };

  useEffect(() => {
    if (user?.user?.isAdmin !== 1) {
      navigate("/");
      return;
    }
    setViewMode("admin");
  }, [user, navigate, setViewMode]);

  const handleUserClick = (user: EventUser) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const filteredUsers: EventUser[] =
    (EventUsers?.users as EventUser[])?.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return (
        fullName.includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }) || [];

  const escapeCSV = (value: string | number | undefined | null): string => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadCSV = async () => {
    if (!filteredUsers.length) return;

    setIsDownloadingCSV(true);
    try {
      // Fetch full user data for each user
      const usersWithFullData: EventUser[] = await Promise.all(
        filteredUsers.map(async (eventUser) => {
          try {
            // Fetch full user profile using userId as objectId
            const response = await axiosInstance<Users>(
              getAPIURL(
                `/User/ByObjectId/${eventUser.userId}?id=${eventUser.userId}`
              )
            );
            const fullUserData = response.data?.user;
            const imageUrls = response.data?.imageUrls;

            // Merge event-specific data with full user data
            return {
              ...eventUser,
              ...fullUserData,
              ...imageUrls,
              // Ensure event-specific fields are preserved
              stageOfBusiness: eventUser.stageOfBusiness,
              eventObjectives: eventUser.eventObjectives,
              sectorsOfInterest: eventUser.sectorsOfInterest,
              consentToShareProfile: eventUser.consentToShareProfile,
            } as EventUser;
          } catch (error) {
            console.error(
              `Error fetching user data for ${eventUser.userId}:`,
              error
            );
            // Return event user data only if fetch fails
            return eventUser;
          }
        })
      );

      const csvRows = [];

      csvRows.push(`Event: ${eventsData?.name}`);

      csvRows.push("");

      // Define all possible user fields in order (excluding IDs, boolean 0/1 fields, and createdDate)
      const fieldMappings: Array<{
        key: string;
        label: string;
        getValue?: (user: any) => any;
      }> = [
        // Basic Info
        { key: "firstName", label: "First Name" },
        { key: "lastName", label: "Last Name" },
        { key: "email", label: "Email" },
        { key: "phoneNumber", label: "Phone Number" },
        { key: "dateOfBirth", label: "Date of Birth" },
        { key: "gender", label: "Gender" },
        { key: "personalGender", label: "Personal Gender" },

        // Location
        { key: "city", label: "City" },
        { key: "county", label: "County" },
        { key: "country", label: "Country" },

        // Personal Profile
        { key: "aboutYou", label: "About You" },
        { key: "personalAbout", label: "Personal About" },
        { key: "languagePreference", label: "Language Preference" },
        {
          key: "personalLanguagePreference",
          label: "Personal Language Preference",
        },
        { key: "personalPositionCompany", label: "Position/Company" },
        {
          key: "PersonalProfessionalPhoto",
          label: "Personal Professional Photo URL",
        },
        { key: "ProfessionalPhoto", label: "Professional Photo URL" },
        { key: "IdDocument", label: "ID Document URL" },

        // Education
        { key: "educationLevel", label: "Education Level" },
        { key: "studyInterest", label: "Study Interest" },
        { key: "universityCollege", label: "University/College" },

        // Skills & Experience
        { key: "skillsExperience", label: "Skills & Experience" },

        // Mentorship
        { key: "mentorshipObjectives", label: "Mentorship Objectives" },
        { key: "mentorshipExpertise", label: "Mentorship Expertise" },
        { key: "mentorshipFormat", label: "Mentorship Format" },

        // Platform & Networking
        { key: "platformInterest", label: "Platform Interest" },
        { key: "peerNetworking", label: "Peer Networking" },
        { key: "productInterested", label: "Product Interested" },

        // Business Information
        { key: "businessName", label: "Business Name" },
        { key: "businessSectorIndustry", label: "Business Sector/Industry" },
        { key: "businessWebsiteUrl", label: "Business Website URL" },
        { key: "businessAbout", label: "Business About" },
        { key: "businessAddressLineOne", label: "Business Address Line 1" },
        { key: "businessAddressLineTwo", label: "Business Address Line 2" },
        { key: "businessCountry", label: "Business Country" },
        {
          key: "businessCountryOperation",
          label: "Business Country Operation",
        },
        { key: "businessPostCode", label: "Business Post Code" },
        { key: "businessTelephoneNumber", label: "Business Telephone" },
        { key: "businessCurrency", label: "Business Currency" },
        { key: "businessTurnover", label: "Business Turnover" },
        { key: "businessObjectives", label: "Business Objectives" },
        { key: "businessProductServices", label: "Business Products/Services" },
        { key: "businessDiscount", label: "Business Discount" },
        { key: "businessDiscountCode", label: "Business Discount Code" },
        { key: "BusinessLogo", label: "Business Logo URL" },

        // Event-specific fields
        { key: "stageOfBusiness", label: "Stage of Business" },
        { key: "eventObjectives", label: "Event Objectives" },
        { key: "sectorsOfInterest", label: "Sectors of Interest" },

        // Membership
        { key: "membershipName", label: "Membership Name" },
        {
          key: "membershipType",
          label: "Membership Type",
          getValue: (user: any) => {
            if (user.isAnnualMembership === 1) return "Annual";
            if (user.isAnnualMembership === 0) return "Monthly";
            return "";
          },
        },

        // Discounts & Referrals
        { key: "DiscountCode", label: "Discount Code" },
        { key: "DiscountEndDate", label: "Discount End Date" },
        { key: "referralCode", label: "Referral Code" },
        { key: "referralCodeUsed", label: "Referral Code Used" },

        // Images
        { key: "BannerImage", label: "Banner Image URL" },

        // Activity
        { key: "lastLoggedIn", label: "Last Logged In" },
      ];

      // Build CSV header
      const headers = fieldMappings.map((field) => field.label);
      csvRows.push(headers.join(","));

      // Build CSV rows with full user data
      usersWithFullData.forEach((user) => {
        const row = fieldMappings.map((field) => {
          let value;
          if (field.getValue) {
            value = field.getValue(user);
          } else {
            value = (user as any)[field.key];
          }
          return escapeCSV(value);
        });
        csvRows.push(row.join(","));
      });

      const csvData = new Blob([csvRows.join("\n")], { type: "text/csv" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(csvData);
      link.download = `${eventsData?.name}_users.csv`;
      link.click();
    } catch (error) {
      console.error("Error generating CSV:", error);
      alert(t("admin.eventDetails.csvFailed"));
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  return (
    <Container fluid className="px-0 pb-4">
      <h2 className="mb-3">{eventsData?.name}</h2>
      <p>
        <strong>{t("admin.eventDetails.description")}:</strong> {eventsData?.description}
      </p>

      <Form.Control
        type="text"
        placeholder={t("admin.eventDetails.searchUsers")}
        className="my-3"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="d-flex flex-column flex-md-row justify-content-between ">
        <h4 className="">{t("admin.eventDetails.bookedUsers")} ({EventUsers?.users?.length || 0})</h4>

        <Button
          onClick={downloadCSV}
          className="d-flex gap-2 align-items-center justify-content-center"
          size="sm"
          disabled={isDownloadingCSV}
        >
          {isDownloadingCSV ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              {t("admin.eventDetails.downloading")}
            </>
          ) : (
            <>
              {t("admin.eventDetails.downloadUsersList")}
              <LuDownload className="cursor-pointer" />
            </>
          )}
        </Button>
      </div>
      <Card className="mt-3">
        <ListGroup variant="flush">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user, idx) => (
              <ListGroup.Item
                key={idx}
                className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2 small"
              >
                <div
                  className="min-w-0"
                  onClick={() => handleUserClick(user)}
                  style={{ cursor: "pointer", flex: 1 }}
                >
                  <strong>
                    {user.firstName} {user.lastName}
                  </strong>{" "}
                  - {user.email}
                  {user.eventObjectives ? (
                    <Badge bg="light" text="dark" className="ms-2 fw-normal">
                      {getEventObjectiveDisplayLabel(user.eventObjectives, t)}
                    </Badge>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  className="text-wrap lh-sm align-self-md-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedUser(user);
                    setCancelModal(true);
                  }}
                >
                  {t("admin.eventDetails.cancelBooking")}
                </Button>
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item>{t("connections.noUsersFound")}</ListGroup.Item>
          )}
        </ListGroup>
      </Card>

      {/* Cancel Confirmation Modal */}
      <Modal show={cancelModal} centered onHide={() => setCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.eventDetails.cancelBooking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("admin.eventDetails.confirmCancelFor")}{" "}
          {selectedUser?.firstName} {selectedUser?.lastName}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCancelModal(false)}>
            {t("admin.eventDetails.noKeepBooking")}
          </Button>
          <Button variant="danger" onClick={handleCancelBooking}>
            {t("admin.eventDetails.yesCancelBooking")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.eventDetails.userDetails")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                <strong>{t("admin.eventDetails.name")}:</strong> {selectedUser.firstName}{" "}
                {selectedUser.lastName}
              </p>
              <p>
                <strong>{t("admin.eventDetails.email")}:</strong> {selectedUser.email}
              </p>
              <p>
                <strong>{t("events.eventObjectives")}:</strong>{" "}
                {getEventObjectiveDisplayLabel(selectedUser.eventObjectives, t)}
              </p>
              <p>
                <strong>{t("events.stageOfBusiness")}:</strong>{" "}
                {getEventStageDisplayLabel(selectedUser.stageOfBusiness, t)}
              </p>
              <p>
                <strong>{t("events.sectorsOfInterest")}:</strong>{" "}
                {selectedUser.sectorsOfInterest}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ✅ NEW Refund Instruction Modal */}
      <Modal
        show={showRefundModal}
        onHide={() => setShowRefundModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.eventDetails.bookingCancelled")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{t("admin.eventDetails.important")}:</strong> {t("admin.eventDetails.refundManual")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowRefundModal(false)}>
            {t("common.done")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EventDetails;
